import { colors, fontSizeSmall } from '@atlaskit/theme';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import commonMessages from '../../../../common/messages';
import type { ChartCustomInfo, Hours } from '../../../../common/types';
import { useFormatDurationFromSeconds } from '../../../../common/utils/date-range';
import { issueIcon, upIcon, downIcon } from './assets/icons';
import messages from './messages';

type HtmlString = string;
type ChartDataPoint = {
	value: [number, Hours, Hours];
	customInfo: ChartCustomInfo;
};
type TooltipFormatterParams = {
	data: ChartDataPoint;
};

export type TooltipFormatter = (arg1: TooltipFormatterParams) => HtmlString;

const getComparisonIcon = (
	cycleTimeInSeconds: number,
	wholePeriodCycleTimeInSeconds: number | null,
): HtmlString => {
	if (wholePeriodCycleTimeInSeconds === null) return '';

	if (cycleTimeInSeconds < wholePeriodCycleTimeInSeconds) {
		return `<span style="${iconGood}">${downIcon}</span>`;
	}

	if (cycleTimeInSeconds > wholePeriodCycleTimeInSeconds) {
		return `<span style="${iconWarn}">${upIcon}</span>`;
	}

	return '';
};

export const useTooltipFormatter = (medianInSeconds: number | null): TooltipFormatter => {
	const { formatMessage, formatDate } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();

	return ({ data }) => {
		if (data == null) {
			return '';
		}

		const {
			value: [deployedDateInMilliSec, _cycleTimeYPosition, _cycleTime],
			customInfo,
		} = data;

		// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'.
		if (customInfo.loading || customInfo.error) {
			return '';
		}

		// @ts-expect-error - TS2339 - Property 'issueKey' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'. | TS2339 - Property 'numCommits' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'. | TS2339 - Property 'numPullRequests' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'. | TS2339 - Property 'reviewTime' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'. | TS2339 - Property 'tooltipFormatCycleTime' does not exist on type 'ChartCustomInfoLoaded | ChartCustomInfoError'.
		const { issueKey, numCommits, numPullRequests, reviewTime, tooltipFormatCycleTime } =
			customInfo;

		const comparisonIcon = getComparisonIcon(tooltipFormatCycleTime, medianInSeconds);
		const cycleTimeMessage = formatDurationFromSeconds(tooltipFormatCycleTime, {
			days: 'short',
			hours: 'short',
		});
		const reviewTimeDuration = formatDurationFromSeconds(reviewTime ?? 0, {
			days: 'short',
			hours: 'short',
		});
		const reviewTimeMessage = `${reviewTimeDuration} ${formatMessage(messages.reviewTime)}`;
		const pullRequestCountMessage = formatMessage(commonMessages.pullRequestsCount, {
			value: numPullRequests,
		});
		const commitCountsMessage = formatMessage(commonMessages.commitCount, {
			value: numCommits,
		});
		const deployedDateMessage = formatDate(new Date(deployedDateInMilliSec), {
			month: 'short',
			day: 'numeric',
		});

		return `
            <div style="${container}">
                <div style="${title}">
                    <div style="${titleIssueIcon}">
                        ${issueIcon}
                    </div>
                    <div style="${titleMain}">
                        <div style="${titleIssueLink}">${issueKey}</div>
                        <div style="${titleComparison}">
                            ${comparisonIcon}
                            ${cycleTimeMessage}
                        </div>
                    </div>
                </div>
                <div style="${detailContainer}">
                    ${
											reviewTime !== null
												? `<div style="${detailMessage}">${reviewTimeMessage}</div>`
												: ''
										}
                    <div style="${detailMessage}">${pullRequestCountMessage}</div>
                    <div style="${detailMessage}">${commitCountsMessage}</div>
                    <div style="${detailMessage}">${deployedDateMessage}</div>
                </div>
            </div>`;
	};
};

const container = `
    border-radius: 3px;
    padding: ${gridSize / 2}px;
    min-width: ${gridSize * 25}px;
    background-color: ${colors.N900};
    color: ${colors.N0};
    font-size: ${fontSizeSmall()}px;
`;

const title = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
    margin-bottom: ${gridSize}px;
`;

const titleIssueIcon = `
    margin-top: ${gridSize / 2}px;
    margin-right: ${gridSize / 2}px;
`;

const titleMain = `
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const titleIssueLink = `
    margin-top: -1px;
    text-decoration: underline;
    color: inherit;
`;

const titleComparison = `
    display: flex;
    align-items: center;
`;

const iconBase = `
    transform: scale(0.666);
    margin-top: ${gridSize / 2}px;
    margin-right: -1px;
`;

const iconGood = `
    ${iconBase}
    fill: ${colors.N800};
    color: ${colors.G300}
`;

const iconWarn = `
    ${iconBase}
    fill: ${colors.N900};
    color: ${colors.Y400}
`;

const detailContainer = `
    padding-left: ${gridSize * 2.5}px;
`;

const detailMessage = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
    margin: 2px 0;
`;
